import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { Building2, Check, Pencil, Plus, Search, Trash2, UserPlus, Users } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
  CompanyMember,
  OrgMembership,
} from "../../api/companiesApi";
import type { SyncedPerson } from "../../api/orgSyncApi";
import { useAuthedApi } from "../../api/useAuthedApi";
import { type AuthUser, hasPermission } from "../../auth/useAuth";
import { EmptyState } from "../../components/Common/EmptyState";
import { PageHeader } from "../../components/Common/PageHeader";
import { WorkspaceCard } from "../../components/Common/WorkspaceCard";
import { CreateOrgDialog } from "../../components/Organization/CreateOrgDialog";
import { AddMemberDialog } from "../../components/Organization/AddMemberDialog";
import { EditMemberRolesDialog } from "../../components/Organization/EditMemberRolesDialog";
import { humanRole } from "../../components/Organization/roleLabel";
import type { RolesResponse } from "../../api/rolesApi";

function message(e: unknown): string {
  return e instanceof Error && e.message ? e.message : "Something went wrong";
}

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

/** The members content. Exported bare for unit tests; the route renders it
 *  inside the shared WorkspaceLayout so it gets the sidebar + inset padding
 *  like every other workspace page. */
export function CompanyMembersPage({ embedded = false }: { embedded?: boolean }) {
  const { companies, auth, orgSync, roles: rolesApi } = useAuthedApi();
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersPage, setMembersPage] = useState(0);
  const [synced, setSynced] = useState<SyncedPerson[]>([]);
  const [syncedLoading, setSyncedLoading] = useState(true);
  const [syncedTotal, setSyncedTotal] = useState(0);
  const [syncedPage, setSyncedPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const searchQueryRef = useRef("");
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [orgs, setOrgs] = useState<OrgMembership[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [roles, setRoles] = useState<RolesResponse | null>(null);
  const [editingMember, setEditingMember] = useState<CompanyMember | null>(null);

  const refreshUser = useCallback(async () => {
    try {
      setUser(await auth.me());
    } catch {
      setUser(null);
    }
  }, [auth]);

  useEffect(() => {
    void refreshUser();
    const onFocus = () => void refreshUser();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshUser]);

  const canInvite = hasPermission(user, "member:invite");
  const canRemove = hasPermission(user, "member:remove");
  // `/v1/roles` is itself gated on roles:manage, so the role column is only
  // fetched (and shown) for people who could also change it.
  const canManageRoles = hasPermission(user, "roles:manage");

  const loadRoles = useCallback(async () => {
    try {
      setRoles(await rolesApi.list());
    } catch (e) {
      // Fail safe: the member list is still useful without role chips.
      console.warn("[members] could not load roles", e);
      setRoles(null);
    }
  }, [rolesApi]);

  useEffect(() => {
    if (canManageRoles) void loadRoles();
  }, [canManageRoles, loadRoles]);

  const rolesByUser = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const h of roles?.holders ?? []) {
      map.set(h.user_id, [...(map.get(h.user_id) ?? []), h.role]);
    }
    for (const list of map.values()) list.sort((a, b) => a.localeCompare(b));
    return map;
  }, [roles]);
  const roleOptions = useMemo(
    () => [...new Set(roles?.roles ?? [])].sort((a, b) => a.localeCompare(b)),
    [roles],
  );

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = searchInput.trim();
      if (next === searchQueryRef.current) return;
      searchQueryRef.current = next;
      setSearchQuery(next);
      setMembersPage(0);
      setSyncedPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const fetchLists = useCallback(async (cancelled: () => boolean) => {
    setMembersLoading(true);
    setSyncedLoading(true);
    const q = searchQuery || undefined;
    try {
      const [m, roster] = await Promise.all([
        companies.members({
          page: membersPage + 1,
          limit: PAGE_SIZE,
          q,
        }),
        orgSync.people({
          page: syncedPage + 1,
          limit: PAGE_SIZE,
          q,
        }),
      ]);
      if (cancelled()) return;
      setMembers(m.members);
      setMembersTotal(m.pagination.total);
      setSynced(roster.people);
      setSyncedTotal(roster.pagination.total);
      const maxMembersPage = Math.max(
        0,
        Math.ceil(m.pagination.total / PAGE_SIZE) - 1,
      );
      const maxSyncedPage = Math.max(
        0,
        Math.ceil(roster.pagination.total / PAGE_SIZE) - 1,
      );
      if (membersPage > maxMembersPage) setMembersPage(maxMembersPage);
      if (syncedPage > maxSyncedPage) setSyncedPage(maxSyncedPage);
    } catch (e) {
      if (cancelled()) return;
      setError(message(e));
      setMembers([]);
      setMembersTotal(0);
      setSynced([]);
      setSyncedTotal(0);
    } finally {
      if (!cancelled()) {
        setMembersLoading(false);
        setSyncedLoading(false);
      }
    }
  }, [companies, membersPage, orgSync, searchQuery, syncedPage]);

  const loadOrgs = useCallback(async () => {
    try {
      const o = await companies.orgs();
      setOrgs(o.orgs);
    } catch {
      setOrgs([]);
    }
  }, [companies]);

  async function onSwitchOrg(companyId: string) {
    setError(null);
    setBusy(true);
    try {
      await companies.switchOrg(companyId);
      window.location.reload();
    } catch (e) {
      setError(message(e));
      setBusy(false);
    }
  }

  useEffect(() => {
    void loadOrgs();
  }, [loadOrgs]);

  useEffect(() => {
    let cancelled = false;
    void fetchLists(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchLists]);

  async function onRemove(userId: string) {
    setError(null);
    try {
      await companies.removeMember(userId);
      await fetchLists(() => false);
    } catch (e) {
      setError(message(e));
    }
  }

  const membersEmpty = !membersLoading && members.length === 0 && membersTotal === 0;
  const syncedEmpty = !syncedLoading && synced.length === 0 && syncedTotal === 0;
  const hasSearch = searchQuery.length > 0;

  return (
    // The Members tab's section scope; display: contents keeps it out of layout.
    <div data-ph-scope="members" style={{ display: "contents" }}>
      {!embedded && (
      <PageHeader
        title="Members"
        subtitle="People in your organization."
        actions={
          canInvite ? (
            <Button
              data-ph="header-open_add_member"
              variant="contained"
              startIcon={<UserPlus size={18} />}
              onClick={() => setAddMemberOpen(true)}
            >
              Add member
            </Button>
          ) : undefined
        }
      />
      )}

      {embedded && canInvite && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button data-ph="header-open_add_member" variant="contained" startIcon={<UserPlus size={18} />} onClick={() => setAddMemberOpen(true)}>
            Add member
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Your organizations (V27 multi-org) ── */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Your organizations
          </Typography>
          <Button
            data-ph="open_create_org"
            size="small"
            variant="outlined"
            startIcon={<Plus size={16} />}
            onClick={() => setCreateOrgOpen(true)}
          >
            Create new org
          </Button>
        </Box>
        <Stack spacing={1.5}>
          {(orgs ?? []).map((o) => (
            <WorkspaceCard key={o.company_id} interactive={false}>
              <Box
                data-ph-scope="org"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <Building2 size={16} />
                  {o.name?.trim() ? (
                    <Typography noWrap>{o.name}</Typography>
                  ) : (
                    <Typography noWrap sx={{ fontStyle: "italic", color: "text.secondary" }}>
                      Untitled organization
                    </Typography>
                  )}
                  {o.roles.map((r) => (
                    <Chip key={r} size="small" label={r} variant="outlined" />
                  ))}
                </Box>
                {o.active ? (
                  // Filled primary chip — same accent token as the Add member
                  // button — so the active org is unmistakable at a glance.
                  <Chip
                    size="small"
                    color="primary"
                    icon={<Check size={14} />}
                    label="Active"
                  />
                ) : (
                  <Button
                    data-ph="select"
                    size="small"
                    variant="text"
                    onClick={() => onSwitchOrg(o.company_id)}
                    disabled={busy}
                    aria-label={`switch to ${o.name?.trim() || "Untitled organization"}`}
                  >
                    Switch
                  </Button>
                )}
              </Box>
            </WorkspaceCard>
          ))}
        </Stack>
      </Box>

      <TextField
        size="small"
        fullWidth
        placeholder="Search by name or email…"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        sx={{ mb: 3, maxWidth: 420 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={16} />
            </InputAdornment>
          ),
        }}
        inputProps={{ "aria-label": "Search members" }}
      />

      {/* ── Members of the active org — headed so the email rows below the
             org list read as members, not stray items. ── */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Members
      </Typography>
      {membersLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : membersEmpty ? (
        <EmptyState
          icon={Users}
          title={hasSearch ? "No matching members" : "No members yet"}
          description={
            hasSearch
              ? `No Artie members match “${searchQuery}”.`
              : "Add teammates by email — they'll receive an invite to join."
          }
        />
      ) : (
        <>
          <Stack spacing={1.5}>
            {members.map((m) => (
              <WorkspaceCard key={m.user_id} interactive={false}>
                <Box data-ph-scope="member" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexWrap: "wrap" }}>
                    <Typography>{m.email ?? m.user_id}</Typography>
                    {canManageRoles &&
                      (rolesByUser.get(m.user_id) ?? []).map((r) => (
                        <Chip key={r} size="small" variant="outlined" label={humanRole(r)} />
                      ))}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                    {canManageRoles && roles && (
                      <IconButton
                        data-ph="edit_roles"
                        size="small"
                        aria-label={`edit roles for ${m.email ?? m.user_id}`}
                        onClick={() => setEditingMember(m)}
                      >
                        <Pencil size={16} />
                      </IconButton>
                    )}
                    {canRemove && (
                      <IconButton
                        data-ph="delete"
                        size="small"
                        aria-label={`remove ${m.email}`}
                        onClick={() => onRemove(m.user_id)}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </WorkspaceCard>
            ))}
          </Stack>
          <TablePagination
            component="div"
            count={membersTotal}
            page={membersPage}
            onPageChange={(_, p) => setMembersPage(p)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
        </>
      )}

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Synced roster
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          People imported from Fetch Org details. Rows with email are
          provisioned as Artie members during fetch.
        </Typography>
        {syncedLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : syncedEmpty ? (
          <EmptyState
            icon={Users}
            title={hasSearch ? "No matching synced people" : "No synced people yet"}
            description={
              hasSearch
                ? `No synced roster rows match “${searchQuery}”.`
                : "Open Departments and click Fetch Org details."
            }
          />
        ) : (
          <>
            <Stack spacing={1.5}>
              {synced.map((p) => (
                <WorkspaceCard key={`${p.plugin_slug ?? ""}:${p.source_key}`} interactive={false}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography>{p.email || p.display_name || p.source_key}</Typography>
                      {p.display_name && p.email && (
                        <Typography variant="caption" color="text.secondary">
                          {p.display_name}
                          {p.role_source_keys?.length
                            ? ` · roles ${p.role_source_keys.join(", ")}`
                            : ""}
                        </Typography>
                      )}
                    </Box>
                    {p.is_artie_member ? (
                      <Chip size="small" label="Artie member" color="primary" variant="outlined" />
                    ) : p.email ? (
                      <Chip size="small" label="No email match" variant="outlined" />
                    ) : (
                      <Chip size="small" label="No email" variant="outlined" />
                    )}
                  </Box>
                </WorkspaceCard>
              ))}
            </Stack>
            <TablePagination
              component="div"
              count={syncedTotal}
              page={syncedPage}
              onPageChange={(_, p) => setSyncedPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          </>
        )}
      </Box>

      {/* Dialogs */}
      <CreateOrgDialog
        open={createOrgOpen}
        onClose={() => setCreateOrgOpen(false)}
        onCreated={() => {
          void loadOrgs();
          void fetchLists(() => false);
        }}
      />
      <AddMemberDialog
        open={addMemberOpen}
        onClose={() => setAddMemberOpen(false)}
        onAdded={() => {
          void fetchLists(() => false);
          if (canManageRoles) void loadRoles();
        }}
      />
      <EditMemberRolesDialog
        member={editingMember}
        currentRoles={editingMember ? (rolesByUser.get(editingMember.user_id) ?? []) : []}
        roleOptions={roleOptions}
        onClose={() => setEditingMember(null)}
        onChanged={() => void loadRoles()}
      />
    </div>
  );
}
